import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, CalendarClock, Check, FileText, Loader2, ShieldCheck, Upload, XCircle,
} from "lucide-react";
import { useI18n } from "../../i18n/LanguageContext";
import { useSeo } from "../../hooks/useSeo";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  nl: {
    eyebrow: "Veilige link", title: "Vul uw dossier aan",
    lead: "Aanvullingen op uw aanvraag worden direct doorgegeven aan uw consultant.",
    missing: "Nog te leveren", complete: "Volledig — dank u",
    country: "Land / regio", city: "Gewenste stad", property_type: "Type pand",
    budget: "Budget", timing: "Wanneer wilt u verhuizen?", phone: "Telefoon",
    message: "Aanvullende info",
    documents: "Documenten", dropHere: "Sleep bestanden hier of klik om te kiezen",
    fileTypes: "PDF, JPG, PNG, DOCX • max. 10 MB",
    save: "Wijzigingen opslaan", saved: "Opgeslagen ✓",
    bookCta: "Plan een gesprek",
    bookEyebrow: "Snel afronden",
    bookHelp: "Plan een korte intake — wij vullen samen de laatste details in.",
    secureNote: "Alleen u en uw consultant hebben toegang tot deze pagina.",
    notFound: "Ongeldige of verlopen link. Neem contact met ons op.",
  },
  en: {
    eyebrow: "Secure link", title: "Complete your file",
    lead: "Anything you add is shared directly with your consultant.",
    missing: "Still to provide", complete: "Complete — thank you",
    country: "Country / region", city: "Desired city", property_type: "Property type",
    budget: "Budget", timing: "When do you want to move?", phone: "Phone",
    message: "Additional info",
    documents: "Documents", dropHere: "Drop files here or click to choose",
    fileTypes: "PDF, JPG, PNG, DOCX • max 10 MB",
    save: "Save changes", saved: "Saved ✓",
    bookCta: "Book a call",
    bookEyebrow: "Wrap it up quickly",
    bookHelp: "Book a short intake — we'll fill in the last details together.",
    secureNote: "Only you and your consultant can access this page.",
    notFound: "Invalid or expired link. Please get in touch.",
  },
  fr: {
    eyebrow: "Lien sécurisé", title: "Complétez votre dossier",
    lead: "Tout ajout est partagé directement avec votre consultant.",
    missing: "Encore à fournir", complete: "Complet — merci",
    country: "Pays / région", city: "Ville souhaitée", property_type: "Type de bien",
    budget: "Budget", timing: "Quand souhaitez-vous emménager ?", phone: "Téléphone",
    message: "Informations complémentaires",
    documents: "Documents", dropHere: "Déposez vos fichiers ici ou cliquez pour choisir",
    fileTypes: "PDF, JPG, PNG, DOCX • max 10 Mo",
    save: "Enregistrer", saved: "Enregistré ✓",
    bookCta: "Réserver un échange",
    bookEyebrow: "Terminez rapidement",
    bookHelp: "Réservez un court entretien — nous remplirons les derniers détails ensemble.",
    secureNote: "Seuls vous et votre consultant accédez à cette page.",
    notFound: "Lien invalide ou expiré. Contactez-nous.",
  },
};

const FIELD_KEYS = ["country", "city", "property_type", "budget", "timing"];

export default function IntakeSelfService() {
  const { token } = useParams();
  const { t, lang } = useI18n();
  const copy = FIELD_LABELS[lang] || FIELD_LABELS.nl;
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  useSeo({ title: copy.title + " — Tip Top Property", path: `/intake/${token}`, lang, noindex: true });

  const [lead, setLead] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    country: "", city: "", property_type: "", budget: "", timing: "", phone: "", message: "",
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/intake/${token}`);
      setLead(data);
      setForm((f) => ({
        country: data.country || f.country,
        city: data.city || f.city,
        property_type: data.property_type || f.property_type,
        budget: data.budget || f.budget,
        timing: data.timing || f.timing,
        phone: data.phone || f.phone,
        message: data.message || f.message,
      }));
    } catch (err) {
      setError(copy.notFound);
    }
  }, [token, copy.notFound]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v && v.trim?.()),
      );
      const { data } = await axios.patch(`${API}/intake/${token}`, payload);
      setLead(data);
      toast.success(copy.saved);
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await axios.post(`${API}/intake/${token}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setLead(data);
        toast.success(`${file.name} ✓`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (error) {
    return (
      <section className="container-wide py-24">
        <div className="card-soft p-12 text-center max-w-xl mx-auto" data-testid="intake-not-found">
          <XCircle className="h-8 w-8 mx-auto text-destructive" />
          <p className="mt-4 text-foreground">{error}</p>
          <Link to="/contact" className="btn-ghost mt-6 inline-flex">{t.common.contact_us}</Link>
        </div>
      </section>
    );
  }

  if (!lead) {
    return (
      <section className="container-wide py-24">
        <div className="text-center text-muted-foreground" data-testid="intake-loading">
          <Loader2 className="h-5 w-5 animate-spin mx-auto" />
        </div>
      </section>
    );
  }

  const missing = lead.missing || [];
  const isComplete = missing.length === 0;

  return (
    <section className="container-wide py-16 md:py-24" data-testid="intake-page">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-accent">
            <ShieldCheck className="h-3.5 w-3.5" />
            {copy.eyebrow}
          </div>
          <h1 className="mt-4 text-4xl md:text-5xl tracking-tight font-medium text-foreground">{copy.title}</h1>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">{copy.lead}</p>

          <div className="mt-8 card-soft p-5">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-muted-foreground">
              {isComplete ? copy.complete : copy.missing}
            </p>
            <div className="mt-3 space-y-2" data-testid="intake-checklist">
              {FIELD_KEYS.map((k) => {
                const done = !missing.includes(k);
                return (
                  <div key={k} className="flex items-center gap-2.5 text-sm">
                    <span className={`h-5 w-5 grid place-items-center rounded-full ${done ? "bg-accent/15 text-accent" : "border border-border text-muted-foreground"}`}>
                      {done ? <Check className="h-3 w-3" /> : <span className="h-1 w-1 rounded-full bg-muted-foreground" />}
                    </span>
                    <span className={done ? "text-foreground" : "text-muted-foreground"}>{copy[k]}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {lead.calendly_url && (
            <div className="mt-6 card-soft p-5 border-accent/40">
              <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-accent">{copy.bookEyebrow}</p>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">{copy.bookHelp}</p>
              <a
                href={`${lead.calendly_url}${lead.calendly_url.includes("?") ? "&" : "?"}name=${encodeURIComponent(lead.name || "")}&email=${encodeURIComponent(lead.email || "")}`}
                target="_blank"
                rel="noopener"
                data-testid="intake-calendly"
                className="btn-primary mt-4 inline-flex"
              >
                <CalendarClock className="h-4 w-4" />
                {copy.bookCta}
              </a>
            </div>
          )}

          <p className="mt-8 text-xs text-muted-foreground">{copy.secureNote}</p>
        </div>

        <div className="lg:col-span-7 space-y-6">
          <div className="card-soft p-6 md:p-8 grid sm:grid-cols-2 gap-5" data-testid="intake-form">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{copy.country}</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="intake-input-country" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{copy.city}</Label>
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="intake-input-city" className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{copy.property_type}</Label>
              <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="intake-input-property-type"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {t.contact.propertyTypes.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{copy.budget}</Label>
              <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
                <SelectTrigger className="mt-1.5" data-testid="intake-input-budget"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {t.contact.budgets.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{copy.timing}</Label>
              <Select value={form.timing} onValueChange={(v) => setForm({ ...form, timing: v })}>
                <SelectTrigger className="mt-1.5" data-testid="intake-input-timing"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {t.contact.timings.map((tt) => (
                    <SelectItem key={tt.value} value={tt.value}>{tt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{copy.phone}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="intake-input-phone" className="mt-1.5" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{copy.message}</Label>
              <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="intake-input-message" className="mt-1.5" />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <button type="button" onClick={save} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="intake-save">
                {saving ? "…" : copy.save}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="card-soft p-6 md:p-8" data-testid="intake-documents">
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">
              {copy.documents}
            </p>
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => {
                e.preventDefault();
                uploadFiles(e.dataTransfer.files);
              }}
              className="mt-4 border-2 border-dashed border-border rounded-md p-8 text-center cursor-pointer hover:border-accent hover:bg-accent/5 transition-colors"
              data-testid="intake-dropzone"
            >
              <Upload className={`h-5 w-5 mx-auto ${uploading ? "animate-pulse text-accent" : "text-muted-foreground"}`} />
              <p className="mt-3 text-sm text-foreground/85">{copy.dropHere}</p>
              <p className="mt-1 text-xs text-muted-foreground">{copy.fileTypes}</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
              onChange={(e) => uploadFiles(e.target.files)}
              data-testid="intake-file-input"
            />
            {(lead.documents || []).length > 0 && (
              <ul className="mt-5 space-y-2" data-testid="intake-doc-list">
                {lead.documents.map((d, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm text-foreground/85 border-t border-border pt-2.5">
                    <FileText className="h-3.5 w-3.5 text-accent" />
                    <span className="truncate flex-1">{d.filename}</span>
                    <span className="text-xs text-muted-foreground">{Math.round((d.size || 0) / 1024)} KB</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
